import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Box,
    CircularProgress,
} from '@mui/material';
import {
    BugReport as BugIcon,
    Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useJednotkaQuery } from '../../api/JsdhNotifikaceService/service/AdminMutations';
import { useSubmitFeedbackMutation, type CreateFeedbackRequest } from '../../api/JsdhNotifikaceService/service/FeedbackMutations';

interface FeedbackDialogProps {
    open: boolean;
    onClose: () => void;
}

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ open, onClose }) => {
    const { user } = useAuth();
    const { data: jednotka } = useJednotkaQuery();
    const submitMutation = useSubmitFeedbackMutation();

    const [typ, setTyp] = useState<'BUG' | 'NAVRH'>('BUG');
    const [kategorie, setKategorie] = useState<'FRONTEND' | 'SYSTEM' | 'JINE'>('FRONTEND');
    const [predmet, setPredmet] = useState('');
    const [popis, setPopis] = useState('');

    const canSubmit = predmet.trim().length > 0 && popis.trim().length > 0;

    const handleSubmit = () => {
        const data: CreateFeedbackRequest = {
            unitCode: jednotka?.zkratka || jednotka?.evidencniCislo || '',
            unitName: jednotka?.nazev,
            autorJmeno: user?.jmeno || '',
            autorEmail: user?.email,
            typ,
            kategorie,
            predmet: predmet.trim(),
            popis: popis.trim(),
            metadata: JSON.stringify({
                platform: 'web',
                userAgent: navigator.userAgent,
                appVersion: '1.7.0',
            }),
        };

        submitMutation.mutate(data, {
            onSuccess: () => {
                setPredmet('');
                setPopis('');
                setTyp('BUG');
                setKategorie('FRONTEND');
                onClose();
            },
        });
    };

    const handleClose = () => {
        if (!submitMutation.isPending) {
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Zpětná vazba</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                            Typ hlášení
                        </Typography>
                        <ToggleButtonGroup
                            value={typ}
                            exclusive
                            onChange={(_e, val) => val && setTyp(val)}
                            fullWidth
                            size="small"
                        >
                            <ToggleButton value="BUG">
                                <BugIcon sx={{ mr: 1, fontSize: 18, color: 'error.main' }} />
                                Nahlásit chybu
                            </ToggleButton>
                            <ToggleButton value="NAVRH">
                                <LightbulbIcon sx={{ mr: 1, fontSize: 18, color: 'warning.main' }} />
                                Návrh na vylepšení
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                            Kategorie
                        </Typography>
                        <ToggleButtonGroup
                            value={kategorie}
                            exclusive
                            onChange={(_e, val) => val && setKategorie(val)}
                            fullWidth
                            size="small"
                        >
                            <ToggleButton value="FRONTEND">Dashboard</ToggleButton>
                            <ToggleButton value="SYSTEM">Celý systém</ToggleButton>
                            <ToggleButton value="JINE">Jiné</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <TextField
                        label="Předmět"
                        value={predmet}
                        onChange={(e) => setPredmet(e.target.value)}
                        fullWidth
                        required
                        inputProps={{ maxLength: 200 }}
                        helperText="Stručný popis problému nebo návrhu"
                    />

                    <TextField
                        label="Podrobný popis"
                        value={popis}
                        onChange={(e) => setPopis(e.target.value)}
                        fullWidth
                        required
                        multiline
                        rows={4}
                        helperText="Popište problém co nejpodrobněji — co se stalo, co jste očekávali, kroky k reprodukci"
                    />

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">
                            Odesílatel: {user?.jmeno || '—'}
                        </Typography>
                        {jednotka?.nazev && (
                            <Typography variant="caption" color="text.secondary">
                                · {jednotka.nazev}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitMutation.isPending}>
                    Zrušit
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!canSubmit || submitMutation.isPending}
                    startIcon={submitMutation.isPending ? <CircularProgress size={16} /> : undefined}
                >
                    {submitMutation.isPending ? 'Odesílám...' : 'Odeslat'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FeedbackDialog;
